import { type Job, type JobCreateInput, type SourcingRun } from "@vidario/domain";

import { type VidarioClient } from "../endpoints";

export interface SendJobInput {
  /** The draft being sent; omitted when the job is sent straight from the create form. */
  jobId?: string;
  input: JobCreateInput;
}

export type SendJobResult =
  /** `run` is null when the start errored but the job had left Draft anyway. */
  { started: true; job: Job; run: SourcingRun | null } | { started: false; job: Job; error: Error };

/** "An Vidario senden": save the job as a Draft, then start its search. Only
 * the started search takes the job out of Draft (#16), so when the start is
 * refused the caller gets the saved Draft back to send again. A failed save
 * throws: nothing was sent.
 *
 * A failed start (timeout, 5xx) may still have started the search, and a retry
 * would start a second paid one. The backend leaves Draft in the same
 * transaction that creates the run, so the job's status decides; if it can't
 * be read either, the start error is thrown. */
export async function sendJob(
  client: VidarioClient,
  { jobId, input }: SendJobInput,
): Promise<SendJobResult> {
  const job = jobId ? await client.updateJob(jobId, input) : await client.createJob(input);
  try {
    const run = await client.startSourcing(job.id);
    return { started: true, job, run };
  } catch (error) {
    const current = await client.getJob(job.id).catch(() => {
      throw error;
    });
    if (current.status !== "draft") return { started: true, job: current, run: null };
    return {
      started: false,
      job: current,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
